/**
 * @license
 * Copyright 2018 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @file User interface for display and editing annotations.
 */

import {ngeRememberPointSize, ngeHasPointSize, ngePointSizeFor} from 'neuroglancer/annotation/nge_point_size';
import './annotations.css';

// @ts-ignore (image import, bundled by esbuild)
import pyrIconUrl from '../../../static/badges/pyr/pyr-icon.png';
import {Annotation, AnnotationId, AnnotationPropertySerializer, AnnotationReference, AnnotationSource, annotationToJson, AnnotationType, annotationTypeHandlers, AxisAlignedBoundingBox, Ellipsoid, formatNumericProperty, isNgeColorProperty, Line, PolyLine} from 'neuroglancer/annotation';
import {displayOrder, pickedAnnotations} from 'neuroglancer/ui/nge_annotation_folders';
import {AnnotationListHost, bindAnnotationListDrag, makeFolderHeading, makePickBar, setRenameNext, takeRenameNext} from 'neuroglancer/ui/nge_annotation_list';
import {SegmentFolder, SegmentFolders} from 'neuroglancer/ui/nge_segment_folders';
import {AnnotationDisplayState, AnnotationLayerState} from 'neuroglancer/annotation/annotation_layer_state';
import {MultiscaleAnnotationSource} from 'neuroglancer/annotation/frontend_source';
import {AnnotationLayer, PerspectiveViewAnnotationLayer, SliceViewAnnotationLayer, SpatiallyIndexedPerspectiveViewAnnotationLayer, SpatiallyIndexedSliceViewAnnotationLayer} from 'neuroglancer/annotation/renderlayer';
import {CoordinateSpace} from 'neuroglancer/coordinate_transform';
import {MouseSelectionState, UserLayer} from 'neuroglancer/layer';
import {LoadedDataSubsource} from 'neuroglancer/layer_data_source';
import {ChunkTransformParameters, getChunkPositionFromCombinedGlobalLocalPositions} from 'neuroglancer/render_coordinate_transform';
import {RenderScaleHistogram, trackableRenderScaleTarget} from 'neuroglancer/render_scale_statistics';
import {RenderLayerRole} from 'neuroglancer/renderlayer';
import {bindSegmentListWidth, registerCallbackWhenSegmentationDisplayStateChanged, SegmentationDisplayState, SegmentWidgetFactory} from 'neuroglancer/segmentation_display_state/frontend';
import {ElementVisibilityFromTrackableBoolean} from 'neuroglancer/trackable_boolean';
import {AggregateWatchableValue, makeCachedLazyDerivedWatchableValue, registerNested, WatchableValue, WatchableValueInterface} from 'neuroglancer/trackable_value';
import {getDefaultAnnotationListBindings} from 'neuroglancer/ui/default_input_event_bindings';
import {LegacyTool, registerLegacyTool} from 'neuroglancer/ui/tool';
import {animationFrameDebounce} from 'neuroglancer/util/animation_frame_debounce';
import {arraysEqual, ArraySpliceOp} from 'neuroglancer/util/array';
import {setClipboard} from 'neuroglancer/util/clipboard';
import {serializeColor, TrackableRGB, unpackRGB, unpackRGBA, useWhiteBackground} from 'neuroglancer/util/color';
import {Borrowed, disposableOnce, RefCounted} from 'neuroglancer/util/disposable';
import {removeChildren} from 'neuroglancer/util/dom';
import {Endianness, ENDIANNESS} from 'neuroglancer/util/endian';
import {ValueOrError} from 'neuroglancer/util/error';
import {vec3} from 'neuroglancer/util/geom';
import {EventActionMap, KeyboardEventBinder, registerActionListener} from 'neuroglancer/util/keyboard_bindings';
import * as matrix from 'neuroglancer/util/matrix';
import {MouseEventBinder} from 'neuroglancer/util/mouse_bindings';
import {formatScaleWithUnitAsString} from 'neuroglancer/util/si_units';
import {NullarySignal, Signal} from 'neuroglancer/util/signal';
import {Uint64} from 'neuroglancer/util/uint64';
import * as vector from 'neuroglancer/util/vector';
import {makeAddButton} from 'neuroglancer/widget/add_button';
import {ColorWidget} from 'neuroglancer/widget/color';
import {makeCopyButton} from 'neuroglancer/widget/copy_button';
import {makeDeleteButton} from 'neuroglancer/widget/delete_button';
import {DependentViewContext, DependentViewWidget} from 'neuroglancer/widget/dependent_view_widget';
import {makeIcon} from 'neuroglancer/widget/icon';
import {makeMoveToButton} from 'neuroglancer/widget/move_to_button';
import {Tab} from 'neuroglancer/widget/tab_view';
import {VirtualList, VirtualListSource} from 'neuroglancer/widget/virtual_list';



export class MergedAnnotationStates extends RefCounted implements
    WatchableValueInterface<readonly AnnotationLayerState[]> {
  changed = new NullarySignal();
  isLoadingChanged = new NullarySignal();
  states: Borrowed<AnnotationLayerState>[] = [];
  relationships: string[] = [];
  private loadingCount = 0;

  get value() {
    return this.states;
  }

  get isLoading() {
    return this.loadingCount !== 0;
  }

  markLoading() {
    this.loadingCount++;
    return () => {
      if (--this.loadingCount === 0) {
        this.isLoadingChanged.dispatch();
      }
    };
  }

  private sort() {
    this.states.sort((a, b) => {
      let d = a.sourceIndex - b.sourceIndex;
      if (d !== 0) return d;
      return a.subsourceIndex - b.subsourceIndex;
    });
  }

  private updateRelationships() {
    const newRelationships = new Set<string>();
    for (const state of this.states) {
      for (const relationship of state.source.relationships) {
        newRelationships.add(relationship);
      }
    }
    this.relationships = Array.from(newRelationships);
  }

  add(state: Borrowed<AnnotationLayerState>) {
    this.states.push(state);
    this.sort();
    this.updateRelationships();
    this.changed.dispatch();
    return () => {
      const index = this.states.indexOf(state);
      this.states.splice(index, 1);
      this.updateRelationships();
      this.changed.dispatch();
    };
  }
}

function getCenterPosition(center: Float32Array, annotation: Annotation) {
  switch (annotation.type) {
    case AnnotationType.AXIS_ALIGNED_BOUNDING_BOX:
    case AnnotationType.LINE:
      vector.add(center, annotation.pointA, annotation.pointB);
      vector.scale(center, center, 0.5);
      break;
    case AnnotationType.POINT:
      center.set(annotation.point);
      break;
    case AnnotationType.ELLIPSOID:
      center.set(annotation.center);
      break;
    case AnnotationType.POLYLINE:
      // The middle of the polyline's first line, as the lab's viewer does.
      if (annotation.points.length >= 2) {
        vector.add(center, annotation.points[0], annotation.points[1]);
        vector.scale(center, center, 0.5);
      } else if (annotation.points.length === 1) {
        center.set(annotation.points[0]);
      }
      break;
  }
}


function setLayerPosition(
    layer: UserLayer, chunkTransform: ValueOrError<ChunkTransformParameters>,
    layerPosition: Float32Array) {
  if (chunkTransform.error !== undefined) return;
  layer.setLayerPosition(chunkTransform.modelTransform, layerPosition);
}


function visitTransformedAnnotationGeometry(
    annotation: Annotation, chunkTransform: ChunkTransformParameters,
    callback: (layerPosition: Float32Array, isVector: boolean) => void) {
  const {layerRank} = chunkTransform;
  const paddedChunkPosition = new Float32Array(layerRank);
  annotationTypeHandlers[annotation.type].visitGeometry(annotation, (chunkPosition, isVector) => {
    // Rank of "chunk" coordinate space may be less than rank of layer space if the annotations are
    // embedded in a higher-dimensional space.  The extra embedding dimensions always are last and
    // have a coordinate of 0.
    paddedChunkPosition.set(chunkPosition);
    const layerPosition = new Float32Array(layerRank);
    (isVector ? matrix.transformVector : matrix.transformPoint)(
        layerPosition, chunkTransform.chunkToLayerTransform, layerRank + 1, paddedChunkPosition,
        layerRank);
    callback(layerPosition, isVector);
  });
}

/** nge: a row of the annotation list: an annotation with its number, or a
 *  folder's heading (ui/nge_annotation_folders.ts). */
type NgeListItem = {
  kind: 'annotation', state: AnnotationLayerState, annotation: Annotation, number: number,
  depth: number, folder?: SegmentFolder, key: string,
}|{
  kind: 'folder', state: AnnotationLayerState, folder: SegmentFolder, depth: number, count: number,
  key: string,
};

const ngeObjectIds = new WeakMap<object, number>();
let ngeNextObjectId = 0;
function ngeObjectId(x: object) {
  let id = ngeObjectIds.get(x);
  if (id === undefined) ngeObjectIds.set(x, id = ++ngeNextObjectId);
  return id;
}

interface AnnotationLayerViewAttachedState {
  refCounted: RefCounted;
  annotations: Annotation[];
  idToIndex: Map<AnnotationId, number>;
  listOffset: number;
}

export class AnnotationLayerView extends Tab {
  private previousSelectedState:
      {annotationId: string, annotationLayerState: AnnotationLayerState, pin: boolean}|undefined =
          undefined;
  private previousHoverId: string|undefined = undefined;
  /** EyeWire II: "12 annotations", beside Show through cells in 3D. */
  private ngeCountEl: HTMLElement|undefined = undefined;
  private previousHoverAnnotationLayerState: AnnotationLayerState|undefined = undefined;

  private virtualListSource: VirtualListSource = {
    length: 0,
    render: (index: number) => this.render(index),
    changed: new Signal<(splices: ArraySpliceOp[]) => void>(),
  };
  private virtualList = new VirtualList({source: this.virtualListSource});
  private listElements: NgeListItem[] = [];
  /** nge: every annotation row in order, those in folded folders too (the
   *  numbers and the arrows go through all of them). */
  private ngeAllAnnotations: NgeListItem[] = [];
  /** nge: where each annotation's row is in the list. */
  private ngeRowIndex = new Map<AnnotationLayerState, Map<AnnotationId, number>>();
  private ngeKeys: string[] = [];
  /** nge: the next redraw replaces every row (columns or positions changed). */
  private ngeRedrawAll = true;
  private ngeRefreshPending = false;
  private ngeNewFolderButton = document.createElement('button');
  private ngePickBar: {element: HTMLElement, update: () => void}|undefined;
  private updated = false;
  private mutableControls = document.createElement('div');
  private headerRow = document.createElement('div');

  get annotationStates() {
    return this.layer.annotationStates;
  }

  private attachedAnnotationStates =
      new Map<AnnotationLayerState, AnnotationLayerViewAttachedState>();

  private updateAttachedAnnotationLayerStates() {
    const states = this.annotationStates.states;
    const {attachedAnnotationStates} = this;
    const newAttachedAnnotationStates =
        new Map<AnnotationLayerState, AnnotationLayerViewAttachedState>();
    for (const [state, info] of attachedAnnotationStates) {
      if (!states.includes(state)) {
        attachedAnnotationStates.delete(state);
        info.refCounted.dispose();
      }
    }
    for (const state of states) {
      const info = attachedAnnotationStates.get(state);
      if (info !== undefined) {
        newAttachedAnnotationStates.set(state, info);
        continue;
      }
      const source = state.source;
      const refCounted = new RefCounted();
      if (source instanceof AnnotationSource) {
        refCounted.registerDisposer(
            source.childAdded.add((annotation) => this.addAnnotationElement(annotation, state)));
        refCounted.registerDisposer(source.childUpdated.add(
            (annotation) => this.updateAnnotationElement(annotation, state)));
        refCounted.registerDisposer(source.childDeleted.add(
            (annotationId) => this.deleteAnnotationElement(annotationId, state)));
        // nge: a new order, or a whole list restored, sends only `changed`
        refCounted.registerDisposer(source.changed.add(this.ngeScheduleRefresh));
      }
      refCounted.registerDisposer(state.transform.changed.add(this.forceUpdateView));
      newAttachedAnnotationStates.set(
          state, {refCounted, annotations: [], idToIndex: new Map(), listOffset: 0});
    }
    this.attachedAnnotationStates = newAttachedAnnotationStates;
    attachedAnnotationStates.clear();
    this.updateCoordinateSpace();
    this.forceUpdateView();
  }

  private forceUpdateView = () => {
    this.updated = false;
    this.ngeRedrawAll = true;
    this.updateView();
  };

  /** nge: the layer's own annotations, the ones folders and dragging act on. */
  private ngeLocalState(): AnnotationLayerState|undefined {
    const local = (this.layer as any).localAnnotations as AnnotationSource|undefined;
    if (local === undefined || local.readonly) return undefined;
    return this.annotationStates.states.find(s => s.source === local);
  }

  private ngeFolders(): SegmentFolders|undefined {
    return (this.layer as any).ngeAnnFolders as SegmentFolders|undefined;
  }

  private ngeRefresh = () => {
    if (!this.visible) {
      this.updated = false;
      return;
    }
    if (!this.updated) {
      this.updateView();
      return;
    }
    this.ngeRebuild();
    this.resetOnUpdate();
  };

  private ngeScheduleRefresh = () => {
    if (this.ngeRefreshPending) return;
    this.ngeRefreshPending = true;
    Promise.resolve().then(() => {
      this.ngeRefreshPending = false;
      if (!this.wasDisposed) this.ngeRefresh();
    });
  };

  /**
   * nge: work out the rows (folders, numbers) and tell the list which ones
   * changed. Rows are compared by a key, so only the rows that differ are
   * drawn again.
   */
  private ngeRebuild() {
    const items: NgeListItem[] = [];
    const all: NgeListItem[] = [];
    const rowIndex = new Map<AnnotationLayerState, Map<AnnotationId, number>>();
    const folders = this.ngeFolders();
    const localState = this.ngeLocalState();
    const picked = pickedAnnotations.ids;
    let number = 0;
    let isMutable = false;
    for (const [state, info] of this.attachedAnnotationStates) {
      if (!state.source.readonly) isMutable = true;
      if (state.chunkTransform.value.error !== undefined) continue;
      const annotations = Array.from(state.source as AnnotationSource);
      info.annotations = annotations;
      const {idToIndex} = info;
      idToIndex.clear();
      for (let i = 0, length = annotations.length; i < length; ++i) {
        idToIndex.set(annotations[i].id, i);
      }
      const rows = new Map<AnnotationId, number>();
      rowIndex.set(state, rows);
      const withFolders = folders !== undefined && state === localState;
      const order = withFolders ? displayOrder(annotations, folders!) :
          annotations.map(annotation => ({kind: 'annotation' as const, annotation, depth: 0, hidden: false, folder: undefined as string|undefined}));
      const present = withFolders ? new Set(annotations.map(a => a.id)) : undefined;
      for (const entry of order) {
        if (entry.kind === 'folder') {
          if (entry.hidden) continue;
          const count = folders!.cellsIn(entry.folder.id).filter(id => present!.has(id)).length;
          items.push({
            kind: 'folder', state, folder: entry.folder, depth: entry.depth, count,
            key: `f:${entry.folder.id}:${ngeObjectId(entry.folder)}:${entry.depth}:${count}:` +
                `${folders!.activeFolder === entry.folder.id ? 1 : 0}:${folders!.isHidden(entry.folder.id) ? 1 : 0}`,
          });
          continue;
        }
        ++number;
        const folder = entry.folder === undefined ? undefined : folders!.value.find(f => f.id === entry.folder);
        const item: NgeListItem = {
          kind: 'annotation', state, annotation: entry.annotation, number, depth: entry.depth, folder,
          key: `a:${ngeObjectId(state)}:${entry.annotation.id}:${ngeObjectId(entry.annotation)}:${number}:` +
              `${entry.depth}:${folder ? ngeObjectId(folder) : ''}:${picked.has(entry.annotation.id) ? 1 : 0}:` +
              `${(state.source as AnnotationSource).ngeHidden?.has(entry.annotation.id) ? 1 : 0}`,
        };
        all.push(item);
        if (entry.hidden) continue;
        rows.set(entry.annotation.id, items.length);
        items.push(item);
      }
    }
    const oldKeys = this.ngeKeys;
    const oldLength = this.virtualListSource.length;
    const newKeys = items.map(x => x.key);
    let pre = 0, suf = 0;
    if (!this.ngeRedrawAll && oldKeys.length === oldLength) {
      while (pre < oldKeys.length && pre < newKeys.length && oldKeys[pre] === newKeys[pre]) ++pre;
      while (suf < oldKeys.length - pre && suf < newKeys.length - pre &&
             oldKeys[oldKeys.length - 1 - suf] === newKeys[newKeys.length - 1 - suf]) {
        ++suf;
      }
    }
    const redrawAll = this.ngeRedrawAll;
    this.ngeRedrawAll = false;
    this.listElements = items;
    this.ngeAllAnnotations = all;
    this.ngeRowIndex = rowIndex;
    this.ngeKeys = newKeys;
    this.updateListLength();
    // room for the longest number
    this.element.style.setProperty('--nge-ann-num-width', `${Math.max(2, String(number).length) + 1}ch`);
    if (redrawAll || pre !== oldLength || pre !== newKeys.length) {
      this.virtualListSource.changed!.dispatch([{
        retainCount: pre, deleteCount: oldLength - pre - suf, insertCount: newKeys.length - pre - suf,
      }]);
    }
    this.mutableControls.style.display = isMutable ? 'contents' : 'none';
    this.ngeNewFolderButton.style.display = localState !== undefined && folders !== undefined ? '' : 'none';
    this.ngePickBar?.update();
  }

  /** nge: open the folders an annotation is folded away in. */
  private ngeUnfoldFor(state: AnnotationLayerState, id: AnnotationId) {
    const folders = this.ngeFolders();
    if (folders === undefined || state !== this.ngeLocalState()) return false;
    let cur = folders.folderOf(id);
    let opened = false;
    for (let guard = 0; cur !== undefined && guard < 100; ++guard) {
      if (cur.collapsed) {
        folders.setCollapsed(cur.id, false);
        opened = true;
      }
      const parent: string|undefined = cur.parent;
      cur = parent === undefined ? undefined : folders.value.find(f => f.id === parent);
    }
    return opened;
  }

  /** nge: what the drag and the pick bar need from this list. */
  private ngeHost: AnnotationListHost = {
    list: this.virtualList.element,
    source: () => this.ngeLocalState()?.source as AnnotationSource|undefined,
    folders: () => this.ngeLocalState() === undefined ? undefined : this.ngeFolders(),
    displayIds: () => {
      const local = this.ngeLocalState();
      return this.ngeAllAnnotations.filter(x => x.state === local).map(
          x => (x as {annotation: Annotation}).annotation.id);
    },
    refresh: () => this.ngeRefresh(),
  };

  private globalDimensionIndices: number[] = [];
  private localDimensionIndices: number[] = [];
  private curCoordinateSpaceGeneration = -1;
  private prevCoordinateSpaceGeneration = -1;
  private columnWidths: number[] = [];
  private gridTemplate: string = '';

  private updateCoordinateSpace() {
    const localCoordinateSpace = this.layer.localCoordinateSpace.value;
    const globalCoordinateSpace = this.layer.manager.root.coordinateSpace.value;
    const globalDimensionIndices: number[] = [];
    const localDimensionIndices: number[] = [];
    for (let globalDim = 0, globalRank = globalCoordinateSpace.rank; globalDim < globalRank;
         ++globalDim) {
      if (this.annotationStates.states.some(state => {
            const transform = state.transform.value;
            if (transform.error !== undefined) return false;
            return transform.globalToRenderLayerDimensions[globalDim] !== -1;
          })) {
        globalDimensionIndices.push(globalDim);
      }
    }
    for (let localDim = 0, localRank = localCoordinateSpace.rank; localDim < localRank;
         ++localDim) {
      if (this.annotationStates.states.some(state => {
            const transform = state.transform.value;
            if (transform.error !== undefined) return false;
            return transform.localToRenderLayerDimensions[localDim] !== -1;
          })) {
        localDimensionIndices.push(localDim);
      }
    }
    if (!arraysEqual(globalDimensionIndices, this.globalDimensionIndices) ||
        !arraysEqual(localDimensionIndices, this.localDimensionIndices)) {
      this.localDimensionIndices = localDimensionIndices;
      this.globalDimensionIndices = globalDimensionIndices;
      ++this.curCoordinateSpaceGeneration;
    }
  }

  constructor(
      public layer: Borrowed<UserLayerWithAnnotations>,
      public displayState: AnnotationDisplayState) {
    super();
    this.element.classList.add('neuroglancer-annotation-layer-view');
    this.registerDisposer(this.visibility.changed.add(() => this.updateView()));
    this.registerDisposer(
        this.annotationStates.changed.add(() => this.updateAttachedAnnotationLayerStates()));
    this.headerRow.classList.add('neuroglancer-annotation-list-header');

    const toolbox = document.createElement('div');
    toolbox.className = 'neuroglancer-annotation-toolbox';

    layer.initializeAnnotationLayerViewTab(this);
    const colorPicker = this.registerDisposer(new ColorWidget(this.displayState.color));
    colorPicker.element.title = 'Change annotation display color';
    this.registerDisposer(new ElementVisibilityFromTrackableBoolean(
        makeCachedLazyDerivedWatchableValue(
            shader => shader.match(/\bdefaultColor\b/) !== null,
            displayState.shaderControls.processedFragmentMain),
        colorPicker.element));
    toolbox.appendChild(colorPicker.element);
    const {mutableControls} = this;
    const pointButton = makeIcon({
      text: annotationTypeHandlers[AnnotationType.POINT].icon,
      title: 'Annotate point',
      onClick: () => {
        displayState.ngeMarker.value = 'dot';
        // The regular dot is the size the player set on the slider, if they
        // ever set one (Ames 2026-10-12); it was always put back to 1.
        displayState.ngeSize.value = ngePointSizeFor(this.layer.managedLayer?.name);
        this.layer.tool.value = new PlacePointTool(this.layer, {});
      },
    });
    // EyeWire II (Amy 2026-10-01): the regular dot stays; the big dot and Pyr
    // are their own buttons. All three place points, and each sets how THIS
    // layer draws them (saved in the layer as "pointMarker" and "pointSize").
    // The Size row below sets the exact size.
    const BIG_SIZE = 5;
    const bigButton = makeIcon({
      text: '●',
      title: 'Annotate point, shown larger (set the size below)',
      onClick: () => {
        displayState.ngeMarker.value = 'dot';
        if (displayState.ngeSize.value <= 1) displayState.ngeSize.value = BIG_SIZE;
        this.layer.tool.value = new PlacePointTool(this.layer, {});
      },
    });
    bigButton.classList.add('nge-big-dot-tool');
    const pyrButton = makeIcon({
      title: 'Annotate point, shown as a Pyr gem in 3D',
      onClick: () => {
        displayState.ngeMarker.value = 'pyr';
        this.layer.tool.value = new PlacePointTool(this.layer, {});
      },
    });
    pyrButton.textContent = '';
    const pyrImg = document.createElement('img');
    pyrImg.src = pyrIconUrl;
    pyrImg.alt = '';
    pyrImg.className = 'nge-pyr-tool-icon';
    pyrButton.appendChild(pyrImg);
    // nge (Ames 2026-10-25): the Pyr gem first, then the dots.
    mutableControls.append(pyrButton, pointButton, bigButton);

    const boundingBoxButton = makeIcon({
      text: annotationTypeHandlers[AnnotationType.AXIS_ALIGNED_BOUNDING_BOX].icon,
      title: 'Annotate bounding box',
      onClick: () => {
        this.layer.tool.value = new PlaceBoundingBoxTool(this.layer, {});
      },
    });
    mutableControls.appendChild(boundingBoxButton);

    const lineButton = makeIcon({
      text: annotationTypeHandlers[AnnotationType.LINE].icon,
      title: 'Annotate line',
      onClick: () => {
        this.layer.tool.value = new PlaceLineTool(this.layer, {});
      },
    });
    mutableControls.appendChild(lineButton);

    const ellipsoidButton = makeIcon({
      text: annotationTypeHandlers[AnnotationType.ELLIPSOID].icon,
      title: 'Annotate ellipsoid',
      onClick: () => {
        this.layer.tool.value = new PlaceEllipsoidTool(this.layer, {});
      },
    });
    mutableControls.appendChild(ellipsoidButton);

    // nge (Ames 2026-10-13): the polyline, for a cilium's centerline. Its
    // icon is drawn (three joined points), like the other Pyr tool buttons.
    const polylineButton = makeIcon({
      title: 'Annotate polyline: Ctrl+click each point along the line, Enter to finish',
      onClick: () => {
        this.layer.tool.value = new PlacePolylineTool(this.layer, {});
      },
    });
    polylineButton.textContent = '';
    polylineButton.classList.add('nge-polyline-tool');
    polylineButton.innerHTML =
        '<svg viewBox="0 0 18 18" width="18" height="18" fill="none" aria-hidden="true">' +
        '<path d="M3 14 L7.5 6.5 L11.5 11 L15 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<circle cx="3" cy="14" r="1.7" fill="currentColor"/><circle cx="7.5" cy="6.5" r="1.4" fill="currentColor"/>' +
        '<circle cx="11.5" cy="11" r="1.4" fill="currentColor"/><circle cx="15" cy="4" r="1.7" fill="currentColor"/></svg>';
    mutableControls.appendChild(polylineButton);
    // nge (Ames 2026-10-25): the shapes on a line of their own, under the
    // points, and the button lit is the tool in hand. It used to follow the
    // point style alone, so picking a box or a line left a point button lit.
    const shapesBreak = document.createElement('div');
    shapesBreak.className = 'nge-ann-tool-break';
    mutableControls.insertBefore(shapesBreak, boundingBoxButton);
    const showMarker = () => {
      const tool = this.layer.tool.value;
      const shape = tool instanceof PlaceBoundingBoxTool ? boundingBoxButton :
          tool instanceof PlaceLineTool ? lineButton :
          tool instanceof PlaceEllipsoidTool ? ellipsoidButton :
          tool instanceof PlacePolylineTool ? polylineButton : undefined;
      const marker = displayState.ngeMarker.value;
      const size = displayState.ngeSize.value;
      pointButton.classList.toggle('nge-marker-on', !shape && marker === 'dot' && size === 1);
      bigButton.classList.toggle('nge-marker-on', !shape && marker === 'dot' && size !== 1);
      pyrButton.classList.toggle('nge-marker-on', !shape && marker === 'pyr');
      for (const b of [boundingBoxButton, lineButton, ellipsoidButton, polylineButton]) {
        b.classList.toggle('nge-marker-on', b === shape);
      }
    };
    this.registerDisposer(displayState.ngeMarker.changed.add(showMarker));
    this.registerDisposer(displayState.ngeSize.changed.add(showMarker));
    this.registerDisposer(this.layer.tool.changed.add(showMarker));
    showMarker();
    toolbox.appendChild(mutableControls);
    this.element.appendChild(toolbox);

    // Point size for this layer: slide or type (Amy 2026-10-01).
    const sizeRow = document.createElement('label');
    sizeRow.className = 'nge-ann-size-row';
    sizeRow.title = 'How big this layer draws its points, as a multiple of the regular dot';
    const sizeLabel = document.createElement('span');
    sizeLabel.textContent = 'Size';
    const sizeSlider = document.createElement('input');
    sizeSlider.type = 'range';
    sizeSlider.min = '0.5'; sizeSlider.max = '10'; sizeSlider.step = '0.5';
    const sizeNumber = document.createElement('input');
    sizeNumber.type = 'number';
    sizeNumber.min = '0.25'; sizeNumber.max = '20'; sizeNumber.step = '0.25';
    const sizeUnit = document.createElement('span');
    sizeUnit.textContent = '×';
    const showSize = () => {
      const v = displayState.ngeSize.value;
      sizeSlider.value = String(v);
      if (document.activeElement !== sizeNumber) sizeNumber.value = String(v);
    };
    const setSize = (raw: string) => {
      const v = Number(raw);
      if (!Number.isFinite(v) || v <= 0) return;
      displayState.ngeSize.value = Math.max(0.25, Math.min(20, v));
    };
    // Only the player's own hand on the slider or the box is remembered,
    // not a size a view or a button set.
    const setByHand = (raw: string) => {
      setSize(raw);
      ngeRememberPointSize(this.layer.managedLayer?.name, displayState.ngeSize.value);
    };
    sizeSlider.addEventListener('input', () => setByHand(sizeSlider.value));
    sizeNumber.addEventListener('input', () => setByHand(sizeNumber.value));
    sizeNumber.addEventListener('blur', showSize);
    // Typing here must not reach the viewer's key bindings.
    sizeNumber.addEventListener('keydown', e => e.stopPropagation());
    this.registerDisposer(displayState.ngeSize.changed.add(showSize));
    showSize();
    sizeRow.append(sizeLabel, sizeSlider, sizeNumber, sizeUnit);
    this.element.appendChild(sizeRow);

    // EyeWire II: a point placed in the 2D view sits in the middle of the
    // branch, so in 3D the cell's own surface hid it (annkri 2026-10-04:
    // "annotation points, not visible in 3d"). This draws the layer over the
    // cells instead. Saved in the layer as "onTop", like Highlight's strokes.
    const topRow = document.createElement('label');
    topRow.className = 'nge-ann-size-row nge-ann-top-row';
    topRow.title = 'Draw this layer over the cells in the 3D view, so annotations inside a branch can be seen';
    const topBox = document.createElement('input');
    topBox.type = 'checkbox';
    const topLabel = document.createElement('span');
    topLabel.textContent = 'Show through cells in 3D';
    const showTop = () => { topBox.checked = displayState.ngeOnTop.value; };
    topBox.addEventListener('change', () => { displayState.ngeOnTop.value = topBox.checked; });
    this.registerDisposer(displayState.ngeOnTop.changed.add(showTop));
    showTop();
    // How many annotations this layer holds (Nik 2026-10-09: a counter per
    // annotation layer, to count true ends without scrolling the list).
    const countEl = this.ngeCountEl = document.createElement('span');
    countEl.className = 'nge-ann-count';
    countEl.title = 'Annotations in this layer';
    // Previous / next annotation (Nseraf 2026-10-10: "arrows that allow you
    // to go to next/previous annotation"). Each press selects the neighbour
    // in the list and moves the view to it, wrapping round at the ends.
    const stepper = document.createElement('span');
    stepper.className = 'nge-ann-stepper';
    const arrow = (delta: number, glyph: string, title: string) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'nge-ann-step';
      b.textContent = glyph;
      b.title = title;
      b.setAttribute('aria-label', title);
      b.addEventListener('mousedown', event => event.stopPropagation());
      b.addEventListener('click', event => { event.stopPropagation(); this.ngeStepAnnotation(delta); });
      return b;
    };
    stepper.append(arrow(-1, '‹', 'Previous annotation'), arrow(1, '›', 'Next annotation'));
    // nge: + Folder (Ames 2026-10-19), as in the Segments panel; picked
    // annotations go straight in.
    const newFolder = this.ngeNewFolderButton;
    newFolder.type = 'button';
    newFolder.className = 'nge-seg-list-keep nge-seg-folder-new nge-ann-folder-new';
    newFolder.textContent = '+ Folder';
    newFolder.title = 'Make a folder, then drag annotations onto it (picked ones go straight in)';
    newFolder.addEventListener('mousedown', event => event.stopPropagation());
    newFolder.addEventListener('click', event => {
      event.stopPropagation();
      const folders = this.ngeFolders();
      const source = this.ngeHost.source();
      if (folders === undefined || source === undefined) return;
      const f = folders.create('New folder');
      setRenameNext(f.id);
      const picked = this.ngeHost.displayIds().filter(id => pickedAnnotations.ids.has(id));
      if (picked.length) {
        folders.move(picked, f.id);
        pickedAnnotations.clear();
      }
      // the heading is drawn at the top: show it
      this.virtualList.element.scrollTop = 0;
    });
    topRow.append(topBox, topLabel, stepper, countEl);
    this.updateListLength();
    this.element.appendChild(topRow);
    // its own line over the list, so the arrows stay centred above
    const listTools = document.createElement('div');
    listTools.className = 'nge-ann-list-tools';
    listTools.appendChild(newFolder);
    this.element.appendChild(listTools);

    this.element.appendChild(this.headerRow);
    const {virtualList} = this;
    virtualList.element.classList.add('neuroglancer-annotation-list');
    this.element.appendChild(virtualList.element);
    // nge: the picked annotations' bar, dragging by the number, and the
    // folders' changes redraw the list.
    this.ngePickBar = makePickBar(this.ngeHost);
    this.element.appendChild(this.ngePickBar.element);
    this.registerDisposer(bindAnnotationListDrag(this.ngeHost));
    this.registerDisposer(pickedAnnotations.changed.add(this.ngeRefresh));
    const folders = this.ngeFolders();
    if (folders !== undefined) this.registerDisposer(folders.changed.add(this.ngeRefresh));
    this.virtualList.element.addEventListener('mouseleave', () => {
      this.displayState.hoverState.value = undefined;
    });

    const bindings = getDefaultAnnotationListBindings();
    this.registerDisposer(new MouseEventBinder(this.virtualList.element, bindings));
    this.virtualList.element.title = bindings.describe();
    this.registerDisposer(this.displayState.hoverState.changed.add(() => this.updateHoverView()));
    this.registerDisposer(
        this.selectedAnnotationState.changed.add(() => this.updateSelectionView()));
    this.registerDisposer(this.layer.localCoordinateSpace.changed.add(() => {
      this.updateCoordinateSpace();
      this.updateView();
    }));
    this.registerDisposer(this.layer.manager.root.coordinateSpace.changed.add(() => {
      this.updateCoordinateSpace();
      this.updateView();
    }));
    this.updateCoordinateSpace();
    this.updateAttachedAnnotationLayerStates();
    this.updateSelectionView();
    // A click on an annotation in the 2D or 3D view asks its list to show the
    // row (src/util/annotation_click_select.ts). A list that has only just
    // been brought forward has no height yet, so it tries until it has.
    const onReveal = (event: Event) => {
      const {state, id} = (event as CustomEvent).detail ?? {};
      let tries = 0;
      const reveal = () => {
        if (this.wasDisposed) return;
        if (tries === 0) this.ngeUnfoldFor(state, id);
        const index = this.ngeRowIndex.get(state)?.get(id) ?? -1;
        if (index >= 0 && this.virtualList.element.offsetHeight > 0) {
          this.virtualList.scrollItemIntoView(index);
          return;
        }
        if (++tries < 20) setTimeout(reveal, 60);
      };
      reveal();
    };
    window.addEventListener('nge-annotation-reveal', onReveal);
    this.registerDisposer(() => window.removeEventListener('nge-annotation-reveal', onReveal));
  }

  private getRenderedAnnotationListElement(
      state: AnnotationLayerState, id: AnnotationId, scrollIntoView: boolean = false): HTMLElement
      |undefined {
    const listIndex = this.ngeRowIndex.get(state)?.get(id);
    if (listIndex === undefined) return undefined;
    if (scrollIntoView) {
      this.virtualList.scrollItemIntoView(listIndex);
    }
    return this.virtualList.getItemElement(listIndex);
  }

  private clearSelectionClass() {
    const {previousSelectedState: state} = this;
    if (state === undefined) return;
    this.previousSelectedState = undefined;
    const element =
        this.getRenderedAnnotationListElement(state.annotationLayerState, state.annotationId);
    if (element !== undefined) {
      element.classList.remove('neuroglancer-annotation-selected');
    }
  }

  private clearHoverClass() {
    const {previousHoverId, previousHoverAnnotationLayerState} = this;
    if (previousHoverAnnotationLayerState !== undefined) {
      this.previousHoverAnnotationLayerState = undefined;
      this.previousHoverId = undefined;
      const element = this.getRenderedAnnotationListElement(
          previousHoverAnnotationLayerState, previousHoverId!!);
      if (element !== undefined) {
        element.classList.remove('neuroglancer-annotation-hover');
      }
    }
  }

  private selectedAnnotationState = makeCachedLazyDerivedWatchableValue((selectionState, pin) => {
    if (selectionState === undefined) return undefined;
    const {layer} = this;
    const layerSelectionState = selectionState.layers.find(s => s.layer === layer)?.state;
    if (layerSelectionState === undefined) return undefined;
    const {annotationId} = layerSelectionState;
    if (annotationId === undefined) return undefined;
    const annotationLayerState = this.annotationStates.states.find(
        x => x.sourceIndex === layerSelectionState.annotationSourceIndex &&
            (layerSelectionState.annotationSubsource === undefined ||
             x.subsourceId === layerSelectionState.annotationSubsource));
    if (annotationLayerState === undefined) return undefined;
    return {annotationId, annotationLayerState, pin};
  }, this.layer.manager.root.selectionState, this.layer.manager.root.selectionState.pin);

  /** nge: `scroll` false when the list was only redrawn (a folder opened,
   *  closed or hidden, rows picked): the selected row is marked again but the
   *  list stays where it is. It went to the selected row, often the newest
   *  annotation at the bottom (annkri, report 234). */
  private updateSelectionView(scroll = true) {
    const selectionState = this.selectedAnnotationState.value;
    const {previousSelectedState} = this;
    if (previousSelectedState === selectionState ||
        (previousSelectedState !== undefined && selectionState !== undefined &&
         previousSelectedState.annotationId === selectionState.annotationId &&
         previousSelectedState.annotationLayerState === selectionState.annotationLayerState &&
         previousSelectedState.pin === selectionState.pin)) {
      return;
    }
    this.clearSelectionClass();
    this.previousSelectedState = selectionState;
    if (selectionState === undefined) return;
    const element = this.getRenderedAnnotationListElement(
        selectionState.annotationLayerState, selectionState.annotationId,
        /*scrollIntoView=*/scroll && selectionState.pin);
    if (element !== undefined) {
      element.classList.add('neuroglancer-annotation-selected');
    }
  }

  private updateHoverView() {
    const selectedValue = this.displayState.hoverState.value;
    let newHoverId: string|undefined;
    let newAnnotationLayerState: AnnotationLayerState|undefined;
    if (selectedValue !== undefined) {
      newHoverId = selectedValue.id;
      newAnnotationLayerState = selectedValue.annotationLayerState;
    }
    const {previousHoverId, previousHoverAnnotationLayerState} = this;
    if (newHoverId === previousHoverId &&
        newAnnotationLayerState === previousHoverAnnotationLayerState) {
      return;
    }
    this.clearHoverClass();
    this.previousHoverId = newHoverId;
    this.previousHoverAnnotationLayerState = newAnnotationLayerState;
    if (newHoverId === undefined) return;
    const element = this.getRenderedAnnotationListElement(newAnnotationLayerState!, newHoverId);
    if (element === undefined) return;
    element.classList.add('neuroglancer-annotation-hover');
  }

  private render(index: number) {
    const item = this.listElements[index];
    if (item.kind === 'folder') {
      const folders = this.ngeFolders()!;
      return makeFolderHeading(
          item.folder, item.depth, item.count, folders, item.state.source as AnnotationSource,
          takeRenameNext(item.folder.id));
    }
    const {annotation, state} = item;
    const {layer, gridTemplate, globalDimensionIndices, localDimensionIndices} = this;
    const [element, elementColumnWidths] = makeAnnotationListElement(layer, annotation, state, gridTemplate, globalDimensionIndices, localDimensionIndices);
    // nge: the number, in the list's order (Ames 2026-10-19). On the layer's
    // own annotations it is also the handle to drag the row by.
    const num = document.createElement('div');
    num.className = 'nge-ann-num';
    num.textContent = String(item.number);
    num.style.gridColumn = 'num';
    num.style.gridRow = '1';
    element.prepend(num);
    if (state === this.ngeLocalState()) {
      element.dataset.ngePickable = '';
      num.title = `Annotation ${item.number}. Drag the row to move it, or onto a folder to put it in. Ctrl+click picks several.`;
    } else {
      num.title = `Annotation ${item.number}`;
    }
    element.classList.toggle('nge-ann-picked', pickedAnnotations.ids.has(annotation.id));
    // in a hidden folder: still listed, dimmed (report 225)
    element.classList.toggle('nge-ann-hidden', !!(state.source as AnnotationSource).ngeHidden?.has(annotation.id));
    if (item.depth > 0) {
      element.classList.add('nge-ann-in-folder');
      element.style.setProperty('--nge-ann-depth', String(item.depth));
      element.style.setProperty('--nge-folder-color', item.folder?.color ?? '#8cb4eb');
    }
    for (const [column, width] of elementColumnWidths.entries()) {
      this.setColumnWidth(column, width);
    }
    element.addEventListener('mouseenter', () => {
      this.displayState.hoverState.value = {
        id: annotation.id,
        partIndex: 0,
        annotationLayerState: state,
      };
    });
    const selectionState = this.selectedAnnotationState.value;
    if (selectionState !== undefined && selectionState.annotationLayerState === state &&
        selectionState.annotationId === annotation.id) {
      element.classList.add('neuroglancer-annotation-selected');
    }
    return element;
  }

  private setColumnWidth(column: number, width: number) {
    // Padding
    width += 2;
    const {columnWidths} = this;
    if (columnWidths[column] > width) {
      // False if `columnWidths[column] === undefined`.
      return;
    }
    columnWidths[column] = width;
    this.element.style.setProperty(`--neuroglancer-column-${column}-width`, `${width}ch`);
  }

  private updateView() {
    if (!this.visible) {
      return;
    }
    if (this.curCoordinateSpaceGeneration !== this.prevCoordinateSpaceGeneration) {
      this.updated = false;
      const {columnWidths} = this;
      columnWidths.length = 0;
      const {headerRow} = this;
      const symbolPlaceholder = document.createElement('div');
      symbolPlaceholder.style.gridColumn = `symbol`;

      const deletePlaceholder = document.createElement('div');
      deletePlaceholder.style.gridColumn = `delete`;

      removeChildren(headerRow);
      // nge: the numbers' column
      const numHeader = document.createElement('div');
      numHeader.className = 'nge-ann-num-header';
      numHeader.style.gridColumn = 'num';
      numHeader.textContent = '#';
      headerRow.appendChild(numHeader);
      headerRow.appendChild(symbolPlaceholder);
      let i = 0;
      let gridTemplate = '[num] var(--nge-ann-num-width, 3ch) [symbol] 2ch';
      const addDimension = (coordinateSpace: CoordinateSpace, dimIndex: number) => {
        const dimWidget = document.createElement('div');
        dimWidget.classList.add('neuroglancer-annotations-view-dimension');
        const name = document.createElement('span');
        name.classList.add('neuroglancer-annotations-view-dimension-name');
        name.textContent = coordinateSpace.names[dimIndex];
        const scale = document.createElement('scale');
        scale.classList.add('neuroglancer-annotations-view-dimension-scale');
        scale.textContent = formatScaleWithUnitAsString(
            coordinateSpace.scales[dimIndex], coordinateSpace.units[dimIndex], {precision: 2});
        dimWidget.appendChild(name);
        dimWidget.appendChild(scale);
        dimWidget.style.gridColumn = `dim ${i + 1}`;
        this.setColumnWidth(i, scale.textContent.length + name.textContent.length + 3);
        gridTemplate += ` [dim] var(--neuroglancer-column-${i}-width)`;
        ++i;
        headerRow.appendChild(dimWidget);
      };
      const globalCoordinateSpace = this.layer.manager.root.coordinateSpace.value;
      for (const globalDim of this.globalDimensionIndices) {
        addDimension(globalCoordinateSpace, globalDim);
      }
      const localCoordinateSpace = this.layer.localCoordinateSpace.value;
      for (const localDim of this.localDimensionIndices) {
        addDimension(localCoordinateSpace, localDim);
      }
      headerRow.appendChild(deletePlaceholder);
      // EyeWire II: room for the jump button beside the delete button.
      gridTemplate += ` [delete] 36px`;
      this.gridTemplate = gridTemplate;
      headerRow.style.gridTemplateColumns = gridTemplate;
      this.prevCoordinateSpaceGeneration = this.curCoordinateSpaceGeneration;
      this.ngeRedrawAll = true;
    }
    if (this.updated) {
      return;
    }
    this.ngeRebuild();
    this.resetOnUpdate();
  }

  /** Select the annotation before or after the selected one and go to it. */
  private ngeStepAnnotation(delta: number) {
    // nge: in the numbers' order, through folded folders too (they open)
    const listElements = this.ngeAllAnnotations;
    const n = listElements.length;
    if (!n) return;
    const selected = this.selectedAnnotationState.value;
    const at = selected === undefined ? -1 : listElements.findIndex(
        e => e.state === selected.annotationLayerState &&
            (e as {annotation: Annotation}).annotation.id === selected.annotationId);
    // Nothing selected yet: Next starts at the first, Previous at the last.
    const index = at < 0 ? (delta > 0 ? 0 : n - 1) : (at + delta + n) % n;
    const {annotation, state} = listElements[index] as {annotation: Annotation, state: AnnotationLayerState};
    this.ngeUnfoldFor(state, annotation.id);
    this.layer.selectAnnotation(state, annotation.id, true);
    const row = this.ngeRowIndex.get(state)?.get(annotation.id);
    if (row !== undefined) this.virtualList.scrollItemIntoView(row);
    try {
      const chunkTransform = state.chunkTransform.value as ChunkTransformParameters;
      const {layerRank} = chunkTransform;
      const chunkPosition = new Float32Array(layerRank);
      const layerPosition = new Float32Array(layerRank);
      getCenterPosition(chunkPosition, annotation);
      matrix.transformPoint(
          layerPosition, chunkTransform.chunkToLayerTransform, layerRank + 1, chunkPosition, layerRank);
      setLayerPosition(this.layer, state.chunkTransform.value, layerPosition);
    } catch { /* the layer's transform is not ready: it is still selected */ }
  }

  private updateListLength() {
    // nge: the rows (folder headings too); the count says annotations only
    this.virtualListSource.length = this.listElements.length;
    const length = this.ngeAllAnnotations.length;
    if (this.ngeCountEl) this.ngeCountEl.textContent = `${length.toLocaleString()} ${length === 1 ? 'annotation' : 'annotations'}`;
  }

  private addAnnotationElement(annotation: Annotation, state: AnnotationLayerState) {
    annotation; state;
    // nge: the rows are worked out again (numbers and folders move with any
    // change) and only those that changed are drawn again.
    this.ngeRefresh();
  }

  private updateAnnotationElement(annotation: Annotation, state: AnnotationLayerState) {
    annotation; state;
    // nge: the rows are worked out again (numbers and folders move with any
    // change) and only those that changed are drawn again.
    this.ngeRefresh();
  }

  private deleteAnnotationElement(annotationId: string, state: AnnotationLayerState) {
    annotationId; state;
    // nge: the rows are worked out again (numbers and folders move with any
    // change) and only those that changed are drawn again.
    this.ngeRefresh();
  }

  private resetOnUpdate() {
    this.clearHoverClass();
    this.clearSelectionClass();
    this.updated = true;
    this.updateHoverView();
    this.updateSelectionView(/*scroll=*/false);
  }
}

export class AnnotationTab extends Tab {
  private layerView =
      this.registerDisposer(new AnnotationLayerView(this.layer, this.layer.annotationDisplayState));
  constructor(public layer: Borrowed<UserLayerWithAnnotations>) {
    super();
    const {element} = this;
    element.classList.add('neuroglancer-annotations-tab');
    element.appendChild(this.layerView.element);
  }
}

function getSelectedAssociatedSegments(annotationLayer: AnnotationLayerState, getBase = false) {
  let segments: Uint64[][] = [];
  const {relationships} = annotationLayer.source;
  const {relationshipStates} = annotationLayer.displayState;
  for (let i = 0, count = relationships.length; i < count; ++i) {
    const segmentationState = relationshipStates.get(relationships[i]).segmentationState.value;
    if (segmentationState != null) {
      if (segmentationState.segmentSelectionState.hasSelectedSegment) {
        segments[i] = [segmentationState.segmentSelectionState.selectedSegment.clone()];
        if (getBase) {
          segments[i] = [...segments[i], segmentationState.segmentSelectionState.baseSelectedSegment.clone()];
        }
        continue;
      }
    }
    segments[i] = [];
  }
  return segments;
}

abstract class PlaceAnnotationTool extends LegacyTool {
  layer: UserLayerWithAnnotations;
  constructor(layer: UserLayerWithAnnotations, options: any) {
    super(layer);
    options;
  }

  get annotationLayer(): AnnotationLayerState|undefined {
    for (const state of this.layer.annotationStates.states) {
      if (!state.source.readonly) return state;
    }
    return undefined;
  }
}

const ANNOTATE_POINT_TOOL_ID = 'annotatePoint';
const ANNOTATE_LINE_TOOL_ID = 'annotateLine';
const ANNOTATE_BOUNDING_BOX_TOOL_ID = 'annotateBoundingBox';
const ANNOTATE_ELLIPSOID_TOOL_ID = 'annotateSphere';
const ANNOTATE_POLYLINE_TOOL_ID = 'annotatePolyline';

// EyeWire II (Ames 2026-10-06): "when I make an annotation layer it
// automatically selects an annotation point (and let's select point at
// 1.5)". The layer bar and the layer type picker announce a layer the person
// has just made; it gets the point tool, drawn as a dot at size 1.5. Layers
// the app makes itself, or that arrive with a saved view, are never touched.
const NEW_LAYER_POINT_SIZE = 1.5;
function armNewAnnotationLayer(managedLayer: any, triesLeft = 20) {
  const userLayer = managedLayer?.layer;
  const displayState = userLayer?.annotationDisplayState;
  if (!userLayer || !displayState || !userLayer.tool) {
    // the layer object can arrive a moment after the tab does
    if (triesLeft > 0) setTimeout(() => armNewAnnotationLayer(managedLayer, triesLeft - 1), 50);
    return;
  }
  // A Pyr gem, in the layer's golden yellow (Ames 2026-10-18: "let's use
  // golden yellow Pyr gems as the default").
  displayState.ngeMarker.value = 'pyr';
  // 1.5 only until the player has set a size of their own on a slider: a new
  // layer was always forced to 1.5, which undid the remembered size the
  // moment the layer was made (Ames 2026-10-12: "not working").
  const name = managedLayer?.name;
  displayState.ngeSize.value = ngeHasPointSize(name) ? ngePointSizeFor(name) : NEW_LAYER_POINT_SIZE;
  // A layer the player has just made shows through the cells in 3D, so its
  // points are not lost inside the branch they mark.
  displayState.ngeOnTop.value = true;
  userLayer.tool.value = new PlacePointTool(userLayer, {});
}
document.addEventListener('nge:new-annotation-layer', (event: Event) => {
  armNewAnnotationLayer((event as CustomEvent).detail?.managedLayer);
});

export class PlacePointTool extends PlaceAnnotationTool {
  trigger(mouseState: MouseSelectionState) {
    const {annotationLayer} = this;
    if (annotationLayer === undefined) {
      // Not yet ready.
      return;
    }
    if (mouseState.updateUnconditionally()) {
      const point = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);
      if (point === undefined) return;
      const annotation: Annotation = {
        id: '',
        description: '',
        relatedSegments: getSelectedAssociatedSegments(annotationLayer),
        point,
        type: AnnotationType.POINT,
        properties: annotationLayer.source.properties.map(x => x.default),
      };
      const reference = annotationLayer.source.add(annotation, /*commit=*/true);
      this.layer.selectAnnotation(annotationLayer, reference.id, true);
      reference.dispose();
    }
  }

  get description() {
    return `annotate point`;
  }

  toJSON() {
    return ANNOTATE_POINT_TOOL_ID;
  }
}

function getMousePositionInAnnotationCoordinates(
    mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState): Float32Array|
    undefined {
  const chunkTransform = annotationLayer.chunkTransform.value;
  if (chunkTransform.error !== undefined) return undefined;
  const chunkPosition = new Float32Array(chunkTransform.modelTransform.unpaddedRank);
  if (!getChunkPositionFromCombinedGlobalLocalPositions(
          chunkPosition, mouseState.unsnappedPosition, annotationLayer.localPosition.value,
          chunkTransform.layerRank, chunkTransform.combinedGlobalLocalToChunkTransform)) {
    return undefined;
  }
  return chunkPosition;
}

abstract class TwoStepAnnotationTool extends PlaceAnnotationTool {
  inProgressAnnotation: WatchableValue<
      {annotationLayer: AnnotationLayerState, reference: AnnotationReference, disposer: () => void}|
      undefined> = new WatchableValue(undefined);

  abstract getInitialAnnotation(
      mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState): Annotation;
  abstract getUpdatedAnnotation(
      oldAnnotation: Annotation, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState): Annotation;

  trigger(mouseState: MouseSelectionState) {
    const {annotationLayer, inProgressAnnotation} = this;
    if (annotationLayer === undefined) {
      // Not yet ready.
      return;
    }
    if (mouseState.updateUnconditionally()) {
      const updatePointB = () => {
        const state = inProgressAnnotation.value!;
        const reference = state.reference;
        const newAnnotation =
            this.getUpdatedAnnotation(reference.value!, mouseState, annotationLayer);
        if (JSON.stringify(annotationToJson(newAnnotation, annotationLayer.source)) ===
            JSON.stringify(annotationToJson(reference.value!, annotationLayer.source))) {
          return;
        }
        state.annotationLayer.source.update(reference, newAnnotation);
        this.layer.selectAnnotation(annotationLayer, reference.id, true);
      };

      if (inProgressAnnotation.value === undefined) {
        const reference = annotationLayer.source.add(
            this.getInitialAnnotation(mouseState, annotationLayer), /*commit=*/false);
        this.layer.selectAnnotation(annotationLayer, reference.id, true);
        const mouseDisposer = mouseState.changed.add(updatePointB);
        const disposer = () => {
          mouseDisposer();
          reference.dispose();
        };
        inProgressAnnotation.value = {
          annotationLayer,
          reference,
          disposer,
        };
      } else {
        updatePointB();
        const state = inProgressAnnotation.value;
        state.annotationLayer.source.commit(state.reference);
        state.disposer();
        inProgressAnnotation.value = undefined;
      }
    }
  }

  disposed() {
    this.deactivate();
    super.disposed();
  }

  deactivate() {
    const state = this.inProgressAnnotation.value;
    if (state !== undefined) {
      state.annotationLayer.source.delete(state.reference);
      state.disposer();
      this.inProgressAnnotation.value = undefined;
    }
  }
}


abstract class PlaceTwoCornerAnnotationTool extends TwoStepAnnotationTool {
  annotationType: AnnotationType.LINE|AnnotationType.AXIS_ALIGNED_BOUNDING_BOX;

  getInitialAnnotation(mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState):
      Annotation {
    const point = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);
    return <AxisAlignedBoundingBox|Line>{
      id: '',
      type: this.annotationType,
      description: '',
      pointA: point,
      pointB: point,
      properties: annotationLayer.source.properties.map(x => x.default),
    };
  }

  getUpdatedAnnotation(
      oldAnnotation: AxisAlignedBoundingBox|Line, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState): Annotation {
    const point = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);
    if (point === undefined) return oldAnnotation;
    return {...oldAnnotation, pointB: point};
  }
}

export class PlaceBoundingBoxTool extends PlaceTwoCornerAnnotationTool {
  get description() {
    return `annotate bounding box`;
  }

  getUpdatedAnnotation(
      oldAnnotation: AxisAlignedBoundingBox, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState) {
    const result = super.getUpdatedAnnotation(oldAnnotation, mouseState, annotationLayer) as
        AxisAlignedBoundingBox;
    const {pointA, pointB} = result;
    const rank = pointA.length;
    for (let i = 0; i < rank; ++i) {
      if (pointA[i] === pointB[i]) {
        pointB[i] += 1;
      }
    }
    return result;
  }

  toJSON() {
    return ANNOTATE_BOUNDING_BOX_TOOL_ID;
  }
}
PlaceBoundingBoxTool.prototype.annotationType = AnnotationType.AXIS_ALIGNED_BOUNDING_BOX;

export class PlaceLineTool extends PlaceTwoCornerAnnotationTool {
  getBaseSegment = false;

  get description() {
    return `annotate line`;
  }

  private initialRelationships: Uint64[][]|undefined;

  getInitialAnnotation(mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState):
      Annotation {
    const result = super.getInitialAnnotation(mouseState, annotationLayer);
    this.initialRelationships = result.relatedSegments =
        getSelectedAssociatedSegments(annotationLayer, this.getBaseSegment);
    return result;
  }

  getUpdatedAnnotation(
      oldAnnotation: Line|AxisAlignedBoundingBox, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState) {
    const result = super.getUpdatedAnnotation(oldAnnotation, mouseState, annotationLayer);
    const initialRelationships = this.initialRelationships;
    const newRelationships = getSelectedAssociatedSegments(annotationLayer, this.getBaseSegment);
    if (initialRelationships === undefined) {
      result.relatedSegments = newRelationships;
    } else {
      result.relatedSegments = Array.from(newRelationships, (newSegments, i) => {
        const initialSegments = initialRelationships[i];
        newSegments =
            newSegments.filter(x => initialSegments.findIndex(y => Uint64.equal(x, y)) === -1);
        return [...initialSegments, ...newSegments];
      });
    }
    return result;
  }

  toJSON() {
    return ANNOTATE_LINE_TOOL_ID;
  }
}
PlaceLineTool.prototype.annotationType = AnnotationType.LINE;

class PlaceEllipsoidTool extends TwoStepAnnotationTool {
  getInitialAnnotation(mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState):
      Annotation {
    const point = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);

    return <Ellipsoid>{
      type: AnnotationType.ELLIPSOID,
      id: '',
      description: '',
      segments: getSelectedAssociatedSegments(annotationLayer),
      center: point,
      radii: vec3.fromValues(0, 0, 0),
      properties: annotationLayer.source.properties.map(x => x.default),
    };
  }

  getUpdatedAnnotation(
      oldAnnotation: Ellipsoid, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState) {
    const radii = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);
    if (radii === undefined) return oldAnnotation;
    const center = oldAnnotation.center;
    const rank = center.length;
    for (let i = 0; i < rank; ++i) {
      radii[i] = Math.abs(center[i] - radii[i]);
    }
    return <Ellipsoid>{
      ...oldAnnotation,
      radii,
    };
  }
  get description() {
    return `annotate ellipsoid`;
  }

  toJSON() {
    return ANNOTATE_ELLIPSOID_TOOL_ID;
  }
}

// ── Polyline (nge, Ames 2026-10-13) ────────────────────────────────────────
// Ported from seung-lab/neuroglancer (branch cj-polyline-test-fix): a tool
// that takes any number of clicks. Each Ctrl+click adds a point and the line
// follows the mouse to the next one. Enter finishes, Backspace takes the last
// point back, Esc gives the polyline up; clicking the last point again
// finishes too. The keys are listened for only while a polyline is being
// drawn, so nothing else in the game loses Enter or Backspace.

abstract class MultiStepAnnotationTool extends PlaceAnnotationTool {
  inProgressAnnotation: WatchableValue<
      {annotationLayer: AnnotationLayerState, reference: AnnotationReference, disposer: () => void}|
      undefined> = new WatchableValue(undefined);

  abstract getInitialAnnotation(
      mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState): Annotation;
  abstract getUpdatedAnnotation(
      oldAnnotation: Annotation, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState,
      triggered: boolean): {newAnnotation: Annotation, finished: boolean};
  abstract undo(): void;

  /** Called as a polyline starts and as it ends, however it ends. */
  protected placingChanged(_placing: boolean) {}

  trigger(mouseState: MouseSelectionState) {
    const {annotationLayer, inProgressAnnotation} = this;
    if (annotationLayer === undefined) {
      // Not yet ready.
      return;
    }
    if (mouseState.updateUnconditionally()) {
      const updateNextPoint = (triggered: boolean = false) => {
        const state = inProgressAnnotation.value;
        if (state === undefined) return false;
        const reference = state.reference;
        if (reference.value == null) return false;
        const {newAnnotation, finished} =
            this.getUpdatedAnnotation(reference.value, mouseState, annotationLayer, triggered);
        if (JSON.stringify(annotationToJson(newAnnotation, annotationLayer.source)) ===
            JSON.stringify(annotationToJson(reference.value, annotationLayer.source))) {
          return finished;
        }
        state.annotationLayer.source.update(reference, newAnnotation);
        this.layer.selectAnnotation(annotationLayer, reference.id, true);
        return finished;
      };

      if (inProgressAnnotation.value === undefined) {
        const reference = annotationLayer.source.add(
            this.getInitialAnnotation(mouseState, annotationLayer), /*commit=*/ false);
        this.layer.selectAnnotation(annotationLayer, reference.id, true);
        const mouseDisposer = mouseState.changed.add(() => { updateNextPoint(false); });
        const disposer = () => {
          mouseDisposer();
          reference.dispose();
        };
        inProgressAnnotation.value = {
          annotationLayer,
          reference,
          disposer,
        };
        this.placingChanged(true);
      } else {
        const finished = updateNextPoint(true);
        if (finished) this.complete();
      }
    }
  }

  disposed() {
    this.deactivate();
    super.disposed();
  }

  /** Give up the annotation in progress. */
  deactivate() {
    const state = this.inProgressAnnotation.value;
    if (state !== undefined) {
      state.annotationLayer.source.delete(state.reference);
      state.disposer();
      this.inProgressAnnotation.value = undefined;
      this.placingChanged(false);
    }
  }

  /** Keep the annotation in progress as it stands. */
  complete() {
    const state = this.inProgressAnnotation.value;
    if (state === undefined) return;
    state.annotationLayer.source.commit(state.reference);
    state.disposer();
    this.inProgressAnnotation.value = undefined;
    this.placingChanged(false);
  }
}

export class PlacePolylineTool extends MultiStepAnnotationTool {
  getBaseSegment = false;

  private storedRelationships: Uint64[][]|undefined;
  private hintElement: HTMLElement|undefined;

  get description() {
    return `annotate polyline`;
  }

  getInitialAnnotation(mouseState: MouseSelectionState, annotationLayer: AnnotationLayerState):
      Annotation {
    const point = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);
    this.storedRelationships = getSelectedAssociatedSegments(annotationLayer, this.getBaseSegment);
    return <PolyLine>{
      type: AnnotationType.POLYLINE,
      id: '',
      description: '',
      relatedSegments: this.storedRelationships,
      // The second point follows the mouse until the next click.
      points: [point, Float32Array.from(point!)],
      properties: annotationLayer.source.properties.map(x => x.default),
    };
  }

  getUpdatedAnnotation(
      oldAnnotation: PolyLine, mouseState: MouseSelectionState,
      annotationLayer: AnnotationLayerState, triggered: boolean) {
    const point = getMousePositionInAnnotationCoordinates(mouseState, annotationLayer);
    if (point === undefined) return {newAnnotation: oldAnnotation, finished: false};

    const {points} = oldAnnotation;
    let newAnnotation: PolyLine;
    if (!triggered) {
      // The last point is a preview of the one the next click will place.
      newAnnotation = {...oldAnnotation, points: [...points.slice(0, -1), point]};
    } else if (arraysEqual(point, points[points.length - 2])) {
      // A click on the point just placed: that finishes the polyline when
      // there is a line to keep, and is otherwise no new point.
      return {newAnnotation: oldAnnotation, finished: points.length > 2};
    } else {
      // The click's own position becomes a point, and a new preview follows.
      newAnnotation =
          {...oldAnnotation, points: [...points.slice(0, -1), point, Float32Array.from(point)]};
    }
    const finished = false;

    const initialRelationships = this.storedRelationships;
    const newRelationships = getSelectedAssociatedSegments(annotationLayer, this.getBaseSegment);
    if (initialRelationships === undefined) {
      newAnnotation.relatedSegments = newRelationships;
    } else {
      newAnnotation.relatedSegments = Array.from(newRelationships, (newSegments, i) => {
        const initialSegments = initialRelationships[i] ?? [];
        newSegments =
            newSegments.filter(x => initialSegments.findIndex(y => Uint64.equal(x, y)) === -1);
        return [...initialSegments, ...newSegments];
      });
    }
    if (triggered) {
      // What was under the points placed so far stays with the polyline.
      this.storedRelationships = newAnnotation.relatedSegments;
    }
    return {newAnnotation, finished};
  }

  toJSON() {
    return ANNOTATE_POLYLINE_TOOL_ID;
  }

  /** Take off the last point: the preview point when finishing, or the point
   *  placed last when undoing. A polyline keeps at least two points. */
  private removeLastPointFromAnnotation() {
    const state = this.inProgressAnnotation.value;
    if (state === undefined) return;
    const annotation = state.reference.value as PolyLine | null | undefined;
    if (annotation == null) return;
    if (annotation.points.length > 2) {
      state.annotationLayer.source.update(
          state.reference, {...annotation, points: annotation.points.slice(0, -1)});
    }
  }

  /** Points placed by a click so far (the last point is only a preview). */
  private placedPointCount() {
    const annotation = this.inProgressAnnotation.value?.reference.value as PolyLine | null | undefined;
    return annotation == null ? 0 : Math.max(0, annotation.points.length - 1);
  }

  complete() {
    // One placed point is not a line: there is nothing to keep.
    if (this.placedPointCount() < 2) {
      this.deactivate();
      return;
    }
    this.removeLastPointFromAnnotation();
    super.complete();
  }

  undo() {
    // The first point cannot be taken back; Esc gives the whole polyline up.
    if (this.placedPointCount() < 2) return;
    const state = this.inProgressAnnotation.value!;
    const annotation = state.reference.value as PolyLine;
    // Drop the point placed last and let the preview point take its place.
    const points = annotation.points;
    state.annotationLayer.source.update(
        state.reference, {...annotation, points: [...points.slice(0, -2), points[points.length - 1]]});
    this.showHint();
  }

  // Enter, Backspace and Esc, only while a polyline is being drawn.
  private onKey = (event: KeyboardEvent) => {
    if (this.inProgressAnnotation.value === undefined) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
                   target.tagName === 'SELECT' || target.isContentEditable)) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    let handled = true;
    if (event.key === 'Enter') this.complete();
    else if (event.key === 'Backspace') this.undo();
    else if (event.key === 'Escape') this.deactivate();
    else handled = false;
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  };

  private showHint() {
    const el = this.hintElement;
    if (el === undefined) return;
    const n = this.placedPointCount();
    const count = el.querySelector('.nge-polyline-hint-count');
    if (count) count.textContent = `${n} ${n === 1 ? 'point' : 'points'}`;
  }

  protected placingChanged(placing: boolean) {
    if (placing) {
      window.addEventListener('keydown', this.onKey, true);
      if (this.hintElement === undefined) {
        // A strip at the bottom of the views, styled by Pyr
        // (src/widgets/annotations_restyle.css).
        const el = this.hintElement = document.createElement('div');
        el.className = 'nge-polyline-hint';
        el.setAttribute('role', 'status');
        el.innerHTML =
            '<span class="nge-polyline-hint-badge">Polyline</span>' +
            '<span class="nge-polyline-hint-count"></span>' +
            '<span class="nge-polyline-hint-key"><kbd>Ctrl + click</kbd> add a point</span>' +
            '<span class="nge-polyline-hint-key"><kbd>Enter</kbd> finish</span>' +
            '<span class="nge-polyline-hint-key"><kbd>Backspace</kbd> undo</span>' +
            '<span class="nge-polyline-hint-key"><kbd>Esc</kbd> cancel</span>';
        document.body.appendChild(el);
      }
      this.showHint();
    } else {
      window.removeEventListener('keydown', this.onKey, true);
      this.hintElement?.remove();
      this.hintElement = undefined;
    }
  }

  trigger(mouseState: MouseSelectionState) {
    const {annotationLayer} = this;
    if (annotationLayer === undefined) return;
    // A first click that is not on anything the layer can place a point at
    // starts nothing.
    if (this.inProgressAnnotation.value === undefined &&
        (!mouseState.updateUnconditionally() ||
         getMousePositionInAnnotationCoordinates(mouseState, annotationLayer) === undefined)) {
      return;
    }
    super.trigger(mouseState);
    this.showHint();
  }
}

registerLegacyTool(
    ANNOTATE_POLYLINE_TOOL_ID,
    (layer, options) => new PlacePolylineTool(<UserLayerWithAnnotations>layer, options));
registerLegacyTool(
    ANNOTATE_POINT_TOOL_ID,
    (layer, options) => new PlacePointTool(<UserLayerWithAnnotations>layer, options));
registerLegacyTool(
    ANNOTATE_BOUNDING_BOX_TOOL_ID,
    (layer, options) => new PlaceBoundingBoxTool(<UserLayerWithAnnotations>layer, options));
registerLegacyTool(
    ANNOTATE_LINE_TOOL_ID,
    (layer, options) => new PlaceLineTool(<UserLayerWithAnnotations>layer, options));
registerLegacyTool(
    ANNOTATE_ELLIPSOID_TOOL_ID,
    (layer, options) => new PlaceEllipsoidTool(<UserLayerWithAnnotations>layer, options));

const newRelatedSegmentKeyMap = EventActionMap.fromObject({
  'enter': {action: 'commit'},
  'escape': {action: 'cancel'},
});

function makeRelatedSegmentList(
    listName: string, segments: Uint64[],
    segmentationDisplayState: WatchableValueInterface<SegmentationDisplayState|null|undefined>,
    mutate?: ((newSegments: Uint64[]) => void)|undefined) {
  return new DependentViewWidget(
      segmentationDisplayState, (segmentationDisplayState, parent, context) => {
        const listElement = document.createElement('div');
        listElement.classList.add('neuroglancer-related-segment-list');
        if (segmentationDisplayState != null) {
          context.registerDisposer(bindSegmentListWidth(segmentationDisplayState, listElement));
        }
        const headerRow = document.createElement('div');
        headerRow.classList.add('neuroglancer-related-segment-list-header');
        const copyButton = makeCopyButton({
          title: `Copy segment IDs`,
          onClick: () => {
            setClipboard(segments.map(x => x.toString()).join(', '));
          },
        });
        headerRow.appendChild(copyButton);
        let headerCheckbox: HTMLInputElement|undefined;
        if (segmentationDisplayState != null) {
          headerCheckbox = document.createElement('input');
          headerCheckbox.type = 'checkbox';
          headerCheckbox.addEventListener('change', () => {
            const {visibleSegments} = segmentationDisplayState.segmentationGroupState.value;
            const add = segments.some(id => !visibleSegments.has(id));
            for (const id of segments) {
              visibleSegments.set(id, add);
            }
          });
          headerRow.appendChild(headerCheckbox);
        }
        if (mutate !== undefined) {
          const deleteButton = makeDeleteButton({
            title: 'Remove all IDs',
            onClick: () => {
              mutate([]);
            },
          });
          headerRow.appendChild(deleteButton);
        }
        const titleElement = document.createElement('span');
        titleElement.classList.add('neuroglancer-related-segment-list-title');
        titleElement.textContent = listName;
        headerRow.appendChild(titleElement);
        if (mutate !== undefined) {
          const addButton = makeAddButton({
            title: 'Add related segment ID',
            onClick: () => {
              const addContext = new RefCounted();
              const addContextDisposer = context.registerDisposer(disposableOnce(addContext));
              const newRow = document.createElement('div');
              newRow.classList.add('neuroglancer-segment-list-entry');
              newRow.classList.add('neuroglancer-segment-list-entry-new');
              const copyButton = makeCopyButton({});
              copyButton.classList.add('neuroglancer-segment-list-entry-copy');
              newRow.appendChild(copyButton);
              if (segmentationDisplayState != null) {
                const checkbox = document.createElement('input');
                checkbox.classList.add('neuroglancer-segment-list-entry-visible-checkbox');
                checkbox.type = 'checkbox';
                newRow.appendChild(checkbox);
              }
              const deleteButton = makeDeleteButton({
                title: 'Cancel adding new segment ID',
                onClick: () => {
                  addContextDisposer();
                },
              });
              deleteButton.classList.add('neuroglancer-segment-list-entry-delete');
              newRow.appendChild(deleteButton);
              const idElement = document.createElement('input');
              idElement.autocomplete = 'off';
              idElement.spellcheck = false;
              idElement.classList.add('neuroglancer-segment-list-entry-id');
              const keyboardEventBinder = addContext.registerDisposer(
                  new KeyboardEventBinder(idElement, newRelatedSegmentKeyMap));
              keyboardEventBinder.allShortcutsAreGlobal = true;
              const validateInput = () => {
                const id = new Uint64();
                if (id.tryParseString(idElement.value)) {
                  idElement.dataset.valid = 'true';
                  return id;
                } else {
                  idElement.dataset.valid = 'false';
                  return undefined;
                }
              };
              validateInput();
              idElement.addEventListener('input', () => {
                validateInput();
              });
              idElement.addEventListener('blur', () => {
                const id = validateInput();
                if (id !== undefined) {
                  mutate([...segments, id]);
                }
                addContextDisposer();
              });
              registerActionListener(idElement, 'cancel', addContextDisposer);
              registerActionListener(idElement, 'commit', () => {
                const id = validateInput();
                if (id !== undefined) {
                  mutate([...segments, id]);
                }
                addContextDisposer();
              });
              newRow.appendChild(idElement);
              listElement.appendChild(newRow);
              idElement.focus();
              addContext.registerDisposer(() => {
                idElement.value = '';
                newRow.remove();
              });
            },
          });
          headerRow.appendChild(addButton);
        }

        listElement.appendChild(headerRow);

        const rows: HTMLElement[] = [];
        const segmentWidgetFactory = SegmentWidgetFactory.make(
            segmentationDisplayState ?? undefined, /*includeMapped=*/ false);
        for (const id of segments) {
          const row = segmentWidgetFactory.get(id);
          rows.push(row);
          if (mutate !== undefined) {
            const deleteButton = makeDeleteButton({
              title: 'Remove ID',
              onClick: event => {
                mutate(segments.filter(x => !Uint64.equal(x, id)));
                event.stopPropagation();
              },
            });
            deleteButton.classList.add('neuroglancer-segment-list-entry-delete');
            row.children[0].appendChild(deleteButton);
          }
          listElement.appendChild(row);
        }
        if (segmentationDisplayState != null) {
          const updateSegments = context.registerCancellable(animationFrameDebounce(() => {
            const {visibleSegments} = segmentationDisplayState.segmentationGroupState.value;
            let numVisible = 0;
            for (const id of segments) {
              if (visibleSegments.has(id)) {
                ++numVisible;
              }
            }
            for (const row of rows) {
              segmentWidgetFactory.update(row);
            }
            headerCheckbox!.checked = numVisible === segments.length && numVisible > 0;
            headerCheckbox!.indeterminate = (numVisible > 0) && (numVisible < segments.length);
          }));
          updateSegments();
          updateSegments.flush();
          registerCallbackWhenSegmentationDisplayStateChanged(
              segmentationDisplayState, context, updateSegments);
          context.registerDisposer(
              segmentationDisplayState.segmentationGroupState.changed.add(updateSegments));
        }
        parent.appendChild(listElement);
      });
}

const ANNOTATION_COLOR_JSON_KEY = 'annotationColor';
export function UserLayerWithAnnotationsMixin<TBase extends {new (...args: any[]): UserLayer}>(
    Base: TBase) {
  abstract class C extends Base implements UserLayerWithAnnotations {
    annotationStates = this.registerDisposer(new MergedAnnotationStates());
    annotationDisplayState = new AnnotationDisplayState();
    annotationCrossSectionRenderScaleHistogram = new RenderScaleHistogram();
    annotationCrossSectionRenderScaleTarget = trackableRenderScaleTarget(8);
    annotationProjectionRenderScaleHistogram = new RenderScaleHistogram();
    annotationProjectionRenderScaleTarget = trackableRenderScaleTarget(8);

    constructor(...args: any[]) {
      super(...args);
      // EyeWire II (Amy 2026-09-28): annotations are yellow (#edd040, the
      // AnnotationDisplayState default) but the volume bounding box stays dark
      // blue. On image and segmentation layers this colour only ever draws
      // that bounding box (their static data-bounds annotations), so those
      // layers get the dark blue as THEIR default. Replacing the trackable
      // (not setting its value) keeps the default out of saved state.
      if ((this.constructor as any).type !== 'annotation') {
        this.annotationDisplayState.color = new TrackableRGB(vec3.fromValues(0.10, 0.25, 0.60));
      }
      this.annotationDisplayState.color.changed.add(this.specificationChanged.dispatch);
      this.annotationDisplayState.shader.changed.add(this.specificationChanged.dispatch);
      this.annotationDisplayState.shaderControls.changed.add(this.specificationChanged.dispatch);
      // nge: a segmentation layer's Annotations tab only ever held the
      // volume's bounding box, and a tab called Annotations that holds no
      // annotations confused new players (Ames 2026-10-14). Its box controls
      // are in the Render tab (segmentation_display_options_tab.ts).
      if ((this.constructor as any).type !== 'segmentation') {
        this.tabs.add(
            'annotations', {label: 'Annotations', order: 10, getter: () => new AnnotationTab(this)});
      }

      let annotationStateReadyBinding: (() => void)|undefined;

      const updateReadyBinding = () => {
        const isReady = this.isReady;
        if (isReady && annotationStateReadyBinding !== undefined) {
          annotationStateReadyBinding();
          annotationStateReadyBinding = undefined;
        } else if (!isReady && annotationStateReadyBinding === undefined) {
          annotationStateReadyBinding = this.annotationStates.markLoading();
        }
      };
      this.readyStateChanged.add(updateReadyBinding);
      updateReadyBinding();

      const {mouseState} = this.manager.layerSelectedValues;
      this.registerDisposer(mouseState.changed.add(() => {
        if (mouseState.active) {
          const {pickedAnnotationLayer} = mouseState;
          if (pickedAnnotationLayer !== undefined &&
              this.annotationStates.states.includes(pickedAnnotationLayer)) {
            const existingValue = this.annotationDisplayState.hoverState.value;
            if (existingValue === undefined || existingValue.id !== mouseState.pickedAnnotationId!
                || existingValue.partIndex !== mouseState.pickedOffset ||
                existingValue.annotationLayerState !== pickedAnnotationLayer) {
              this.annotationDisplayState.hoverState.value = {
                id: mouseState.pickedAnnotationId!,
                partIndex: mouseState.pickedOffset,
                annotationLayerState: pickedAnnotationLayer,
              };
            }
            return;
          }
        }
        this.annotationDisplayState.hoverState.value = undefined;
      }));
    }

    initializeAnnotationLayerViewTab(tab: AnnotationLayerView) {
      tab;
    }

    restoreState(specification: any) {
      super.restoreState(specification);
      // EyeWire II (Amy 2026-09-28): on image and segmentation layers this
      // colour only draws the volume bounding box, and Spelunker saves
      // "#00ff64" into every state it writes, which turned the box neon green
      // when a sheet Start link loaded. The box stays dark blue here; only
      // annotation layers take their colour from the saved state.
      if ((this.constructor as any).type === 'annotation') {
        this.annotationDisplayState.color.restoreState(specification[ANNOTATION_COLOR_JSON_KEY]);
      }
    }

    captureSelectionState(state: this['selectionState'], mouseState: MouseSelectionState) {
      super.captureSelectionState(state, mouseState);
      const annotationLayer = mouseState.pickedAnnotationLayer;
      if (annotationLayer === undefined ||
          !this.annotationStates.states.includes(annotationLayer)) {
        return;
      }

      state.annotationId = mouseState.pickedAnnotationId;
      state.annotationType = mouseState.pickedAnnotationType;
      state.annotationBuffer = new Uint8Array(
        mouseState.pickedAnnotationBuffer!, mouseState.pickedAnnotationBufferBaseOffset!);
      state.annotationIndex = mouseState.pickedAnnotationIndex!;
      state.annotationCount = mouseState.pickedAnnotationCount!;
      state.annotationInstanceIndex = mouseState.pickedAnnotationInstanceIndex!;
      state.annotationInstanceCount = mouseState.pickedAnnotationInstanceCount!;
      state.annotationPartIndex = mouseState.pickedOffset;
      state.annotationSourceIndex = annotationLayer.sourceIndex;
      state.annotationSubsource = annotationLayer.subsourceId;
    }

    displayAnnotationState(state: this['selectionState'], parent: HTMLElement, context: RefCounted):
        boolean {
      if (state.annotationId === undefined) return false;
      const annotationLayer = this.annotationStates.states.find(
          x => x.sourceIndex === state.annotationSourceIndex &&
               x.subsubsourceId === state.annotationSubsubsourceId &&
              (state.annotationSubsource === undefined ||
               x.subsourceId === state.annotationSubsource));
      if (annotationLayer === undefined) return false;
      const reference =
          context.registerDisposer(annotationLayer.source.getReference(state.annotationId));
      parent.appendChild(
          context
              .registerDisposer(new DependentViewWidget(
                  context.registerDisposer(
                      new AggregateWatchableValue(() => ({
                                                    annotation: reference,
                                                    chunkTransform: annotationLayer.chunkTransform
                                                  }))),
                  ({annotation, chunkTransform}, parent, context) => {
                    let statusText: string|undefined;
                    if (annotation == null) {
                      if (state.annotationType !== undefined &&
                          state.annotationBuffer !== undefined) {
                        const handler = annotationTypeHandlers[state.annotationType];
                        const rank = annotationLayer.source.rank;
                        const numGeometryBytes = handler.serializedBytes(rank);
                        const baseOffset = state.annotationBuffer.byteOffset;
                        const dataView = new DataView(state.annotationBuffer.buffer);
                        const isLittleEndian = Endianness.LITTLE === ENDIANNESS;
                        const {properties} = annotationLayer.source;
                        const annotationPropertySerializer =
                          new AnnotationPropertySerializer(rank, numGeometryBytes, properties);
                        // By instance: a polyline's own lines are instances, and the
                        // one picked is where reading its points begins.
                        let annotationIndex = state.annotationInstanceIndex ?? state.annotationIndex!;
                        const annotationCount =
                            state.annotationInstanceCount ?? state.annotationCount!;
                        const instanceStride = annotationPropertySerializer.propertyGroupBytes[0];
                        if (state.annotationType === AnnotationType.POLYLINE) {
                          // Back to the polyline's first line: each line's uint32
                          // holds its index within the polyline in the low 31 bits.
                          const lineIndex = dataView.getUint32(
                              baseOffset + instanceStride * annotationIndex, isLittleEndian) & 0x7FFFFFFF;
                          annotationIndex -= lineIndex;
                        }
                        annotation = handler.deserialize(
                            dataView, baseOffset + instanceStride * annotationIndex,
                            isLittleEndian, rank, state.annotationId!, instanceStride);
                        annotationPropertySerializer.deserialize(
                            dataView, baseOffset, annotationIndex, annotationCount, isLittleEndian,
                            annotation.properties = new Array(properties.length));
                        if (annotationLayer.source.hasNonSerializedProperties()) {
                          statusText = 'Loading...';
                        }
                      } else {
                        statusText = (annotation === null) ? 'Annotation not found' : 'Loading...';
                      }
                    }
                    if (annotation != null) {
                      const layerRank =
                          chunkTransform.error === undefined ? chunkTransform.layerRank : 0;
                      const positionGrid = document.createElement('div');
                      positionGrid.classList.add(
                          'neuroglancer-selected-annotation-details-position-grid');
                      positionGrid.style.gridTemplateColumns = `[icon] 0fr [copy] 0fr repeat(${
                          layerRank}, [dim] 0fr [coord] 0fr) [move] 0fr [delete] 0fr`;
                      parent.appendChild(positionGrid);

                      const handler = annotationTypeHandlers[annotation.type];
                      const icon = document.createElement('div');
                      icon.className = 'neuroglancer-selected-annotation-details-icon';
                      icon.textContent = handler.icon;
                      positionGrid.appendChild(icon);

                      if (layerRank !== 0) {
                        const {layerDimensionNames} =
                            (chunkTransform as ChunkTransformParameters).modelTransform;
                        for (let i = 0; i < layerRank; ++i) {
                          const dimElement = document.createElement('div');
                          dimElement.classList.add(
                              'neuroglancer-selected-annotation-details-position-dim');
                          dimElement.textContent = layerDimensionNames[i];
                          dimElement.style.gridColumn = `dim ${i + 1}`;
                          positionGrid.appendChild(dimElement);
                        }
                        visitTransformedAnnotationGeometry(
                            annotation, chunkTransform as ChunkTransformParameters,
                            (layerPosition, isVector) => {
                              const copyButton = makeCopyButton({
                                title: 'Copy position',
                                onClick: () => {
                                  setClipboard(layerPosition.map(x => Math.floor(x)).join(', '));
                                },
                              });
                              copyButton.style.gridColumn = 'copy';
                              positionGrid.appendChild(copyButton);
                              for (let layerDim = 0; layerDim < layerRank; ++layerDim) {
                                const coordElement = document.createElement('div');
                                coordElement.classList.add(
                                    'neuroglancer-selected-annotation-details-position-coord');
                                coordElement.style.gridColumn = `coord ${layerDim + 1}`;
                                coordElement.textContent =
                                    Math.floor(layerPosition[layerDim]).toString();
                                positionGrid.appendChild(coordElement);
                              }
                              if (!isVector) {
                                const moveButton = makeMoveToButton({
                                  title: 'Move to position',
                                  onClick: () => {
                                    setLayerPosition(this, chunkTransform, layerPosition);
                                  },
                                });
                                moveButton.style.gridColumn = 'move';
                                positionGrid.appendChild(moveButton);
                              }
                            });
                      }

                      if (!annotationLayer.source.readonly) {
                        const button = makeDeleteButton({
                          title: 'Delete annotation',
                          onClick: () => {
                            annotationLayer.source.delete(reference);
                          }
                        });
                        button.classList.add('neuroglancer-selected-annotation-details-delete');
                        positionGrid.appendChild(button);
                      }

                      const {relationships, properties} = annotationLayer.source;
                      const sourceReadonly = annotationLayer.source.readonly;

                      for (let i = 0, count = properties.length; i < count; ++i) {
                        const property = properties[i];
                        // nge: the folder color is set from the folder
                        if (isNgeColorProperty(property)) continue;
                        const label = document.createElement('label');
                        label.classList.add('neuroglancer-annotation-property');
                        const idElement = document.createElement('span');
                        idElement.classList.add('neuroglancer-annotation-property-label');
                        idElement.textContent = property.identifier;
                        label.appendChild(idElement);
                        const {description} = property;
                        if (description !== undefined) {
                          label.title = description;
                        }
                        const value = annotation.properties[i];
                        const valueElement = document.createElement('span');
                        valueElement.classList.add('neuroglancer-annotation-property-value');
                        switch (property.type) {
                          case 'rgb': {
                            const colorVec = unpackRGB(value);
                            const hex = serializeColor(colorVec);
                            valueElement.textContent = hex;
                            valueElement.style.backgroundColor = hex;
                            valueElement.style.color =
                                useWhiteBackground(colorVec) ? 'white' : 'black';
                            break;
                          }
                          case 'rgba': {
                            const colorVec = unpackRGB(value);
                            valueElement.textContent = serializeColor(unpackRGBA(value));
                            valueElement.style.backgroundColor = serializeColor(unpackRGB(value));
                            valueElement.style.color =
                                useWhiteBackground(colorVec) ? 'white' : 'black';
                            break;
                          }
                          default:
                            valueElement.textContent = formatNumericProperty(property, value);
                            break;
                        }
                        label.appendChild(valueElement);
                        parent.appendChild(label);
                      }

                      const {relatedSegments} = annotation;
                      for (let i = 0, count = relationships.length; i < count; ++i) {
                        const related = relatedSegments === undefined ? [] : relatedSegments[i];
                        if (related.length === 0 && sourceReadonly) continue;
                        const relationshipIndex = i;
                        const relationship = relationships[i];
                        parent.appendChild(
                            context
                                .registerDisposer(makeRelatedSegmentList(
                                    relationship, related,
                                    annotationLayer.displayState.relationshipStates
                                        .get(relationship)
                                        .segmentationState,
                                    sourceReadonly ?
                                        undefined :
                                        newIds => {
                                          const annotation = reference.value;
                                          if (annotation == null) {
                                            return;
                                          }
                                          let {relatedSegments} = annotation;
                                          if (relatedSegments === undefined) {
                                            relatedSegments =
                                                annotationLayer.source.relationships.map(() => []);
                                          } else {
                                            relatedSegments = relatedSegments.slice();
                                          }
                                          relatedSegments[relationshipIndex] = newIds;
                                          const newAnnotation = {...annotation, relatedSegments};
                                          annotationLayer.source.update(reference, newAnnotation);
                                          annotationLayer.source.commit(reference);
                                        }))
                                .element);
                      }

                      if (!annotationLayer.source.readonly || annotation.description) {
                        if (annotationLayer.source.readonly) {
                          const description = document.createElement('div');
                          description.className = 'neuroglancer-annotation-details-description';
                          description.textContent = annotation.description || '';
                          parent.appendChild(description);
                        } else {
                          const description = document.createElement('textarea');
                          description.value = annotation.description || '';
                          description.rows = 3;
                          description.className = 'neuroglancer-annotation-details-description';
                          description.placeholder = 'Description';
                          description.addEventListener('change', () => {
                            const x = description.value;
                            annotationLayer.source.update(
                                reference, {...annotation!, description: x ? x : undefined});
                            annotationLayer.source.commit(reference);
                          });
                          parent.appendChild(description);
                        }
                      }
                    }
                    if (statusText !== undefined) {
                      const statusMessage = document.createElement('div');
                      statusMessage.classList.add('neuroglancer-selection-annotation-status');
                      statusMessage.textContent = statusText;
                      parent.appendChild(statusMessage);
                    }
                  }))
              .element);
      return true;
    }


    displaySelectionState(
        state: this['selectionState'], parent: HTMLElement,
        context: DependentViewContext): boolean {
      let displayed = this.displayAnnotationState(state, parent, context);
      if (super.displaySelectionState(state, parent, context)) displayed = true;
      return displayed;
    }

    addLocalAnnotations(
        loadedSubsource: LoadedDataSubsource, source: AnnotationSource, role: RenderLayerRole) {
      const {subsourceEntry} = loadedSubsource;
      const state = new AnnotationLayerState({
        localPosition: this.localPosition,
        transform: loadedSubsource.getRenderLayerTransform(),
        source,
        displayState: this.annotationDisplayState,
        dataSource: loadedSubsource.loadedDataSource.layerDataSource,
        subsourceIndex: loadedSubsource.subsourceIndex,
        subsourceId: subsourceEntry.id,
        role,
      });
      this.addAnnotationLayerState(state, loadedSubsource);
    }

    addStaticAnnotations(loadedSubsource: LoadedDataSubsource) {
      const {subsourceEntry} = loadedSubsource;
      const {staticAnnotations} = subsourceEntry.subsource;
      if (staticAnnotations === undefined) return false;
      loadedSubsource.activate(() => {
        this.addLocalAnnotations(
            loadedSubsource, staticAnnotations, RenderLayerRole.DEFAULT_ANNOTATION);
      });
      return true;
    }

    addAnnotationLayerState(state: AnnotationLayerState, loadedSubsource: LoadedDataSubsource) {
      const refCounted = loadedSubsource.activated!;
      refCounted.registerDisposer(this.annotationStates.add(state));
      const annotationLayer = new AnnotationLayer(this.manager.chunkManager, state.addRef());
      if (annotationLayer.source instanceof MultiscaleAnnotationSource) {
        const crossSectionRenderLayer = new SpatiallyIndexedSliceViewAnnotationLayer({
          annotationLayer: annotationLayer.addRef(),
          renderScaleTarget: this.annotationCrossSectionRenderScaleTarget,
          renderScaleHistogram: this.annotationCrossSectionRenderScaleHistogram
        });
        refCounted.registerDisposer(
            loadedSubsource.messages.addChild(crossSectionRenderLayer.messages));

        const projectionRenderLayer = new SpatiallyIndexedPerspectiveViewAnnotationLayer({
          annotationLayer: annotationLayer.addRef(),
          renderScaleTarget: this.annotationProjectionRenderScaleTarget,
          renderScaleHistogram: this.annotationProjectionRenderScaleHistogram
        });
        refCounted.registerDisposer(
            loadedSubsource.messages.addChild(projectionRenderLayer.messages));

        refCounted.registerDisposer(registerNested((context, value) => {
          if (value) {
            context.registerDisposer(this.addRenderLayer(crossSectionRenderLayer.addRef()));
            context.registerDisposer(this.addRenderLayer(projectionRenderLayer.addRef()));
          }
        }, this.annotationDisplayState.displayUnfiltered));
      }
      {
        const renderLayer = new SliceViewAnnotationLayer(
            annotationLayer, this.annotationCrossSectionRenderScaleHistogram);
        refCounted.registerDisposer(this.addRenderLayer(renderLayer));
        refCounted.registerDisposer(loadedSubsource.messages.addChild(renderLayer.messages));
      }
      {
        const renderLayer = new PerspectiveViewAnnotationLayer(
            annotationLayer.addRef(), this.annotationProjectionRenderScaleHistogram);
        refCounted.registerDisposer(this.addRenderLayer(renderLayer));
        refCounted.registerDisposer(loadedSubsource.messages.addChild(renderLayer.messages));
      }
    }

    selectAnnotation(
        annotationLayer: Borrowed<AnnotationLayerState>, id: string, pin: boolean|'toggle') {
      this.manager.root.selectionState.captureSingleLayerState(this, state => {
        state.annotationId = id;
        state.annotationSourceIndex = annotationLayer.sourceIndex;
        state.annotationSubsource = annotationLayer.subsourceId;
        state.annotationSubsubsourceId = annotationLayer.subsubsourceId;
        return true;
      }, pin);
    }

    toJSON() {
      const x = super.toJSON();
      x[ANNOTATION_COLOR_JSON_KEY] = this.annotationDisplayState.color.toJSON();
      return x;
    }
  }
  return C;
}

export type UserLayerWithAnnotations =
    InstanceType<ReturnType<typeof UserLayerWithAnnotationsMixin>>;

/** nge: the text Pyr writes on its own marks ("Hits Edge: cell 7205...",
 *  util/highlight.ts) is not a note, and is not shown as one (Ames 2026-11-01).
 *  A note written on the mark takes its place. */
const ngeAutoDescription = /^(?:.*: )?cell \d+\s*$|^Soma of cell \d+|^(?:Soma|AIS), confirmed$/;
function ngeNoteOf(annotation: Annotation): string {
  const text = annotation.description || '';
  return ngeAutoDescription.test(text) ? '' : text;
}

export function makeAnnotationListElement(layer: UserLayerWithAnnotations, annotation: Annotation, state: AnnotationLayerState, gridTemplate: string, globalDimensionIndices: number[], localDimensionIndices: number[]): [HTMLDivElement, number[]] {
    const chunkTransform = state.chunkTransform.value as ChunkTransformParameters;
    const element = document.createElement('div');
    element.classList.add('neuroglancer-annotation-list-entry');
    element.dataset.color = state.displayState.color.toString();
    // EyeWire II: Highlight mode styles the rows of its newest mark by id.
    element.dataset.ngeId = String(annotation.id);
    element.style.setProperty('--nge-ann-color', serializeColor(state.displayState.color.value));
    // nge: an annotation its folder colors shows that color here too
    const ngeColorAt = state.source.properties.findIndex(isNgeColorProperty);
    const ngePainted = ngeColorAt < 0 ? 0 : (annotation.properties[ngeColorAt] ?? 0);
    if (ngePainted !== 0) {
      element.style.setProperty('--nge-ann-color', serializeColor(unpackRGB(ngePainted)));
    }
    element.style.gridTemplateColumns = gridTemplate;
    const icon = document.createElement('div');
    icon.className = 'neuroglancer-annotation-icon';
    icon.textContent = annotationTypeHandlers[annotation.type].icon;
    element.appendChild(icon);

    // EyeWire II (Ames 2026-10-06: "how do I jump to annotations? We should
    // have a jump button"): every row carries a jump button, shown on hover
    // beside the delete button. It does what a right click on the row always
    // did, which nobody could have guessed.
    const rowActions = document.createElement('div');
    rowActions.className = 'nge-ann-row-actions';
    const jumpButton = makeMoveToButton({
      title: 'Jump to this annotation',
      onClick: () => {
        element.dispatchEvent(new CustomEvent('action:move-to-annotation'));
      },
    });
    jumpButton.classList.add('nge-ann-row-jump');
    jumpButton.addEventListener('mousedown', event => event.stopPropagation());
    jumpButton.addEventListener('click', event => event.stopPropagation());
    rowActions.appendChild(jumpButton);

    let deleteButton: HTMLElement|undefined;

    const maybeAddDeleteButton = () => {
      if (state.source.readonly) return;
      if (deleteButton !== undefined) return;
      deleteButton = makeDeleteButton({
        title: 'Delete annotation',
        onClick: event => {
          event.stopPropagation();
          event.preventDefault();
          const ref = state.source.getReference(annotation.id);
          try {
            state.source.delete(ref);
          } finally {
            ref.dispose();
          }
        },
      });
      deleteButton.classList.add('neuroglancer-annotation-list-entry-delete');
      rowActions.appendChild(deleteButton);
    };

    // nge: a pencil on the row writes its note right there, under the row,
    // with the Selection panel left alone (Ames 2026-11-01).
    let noteText: HTMLElement|undefined;
    const openNoteEditor = () => {
      if (element.querySelector('.nge-ann-note-editor')) return;
      const box = document.createElement('textarea');
      box.className = 'nge-ann-note-editor';
      box.rows = 2;
      box.maxLength = 2000;
      box.placeholder = 'Write a note. Enter saves, Esc cancels.';
      box.value = ngeNoteOf(annotation);
      if (noteText) noteText.style.display = 'none';
      let done = false;
      const close = (save: boolean) => {
        if (done) return;
        done = true;
        const text = box.value.trim();
        if (save && text !== ngeNoteOf(annotation)) {
          const ref = state.source.getReference(annotation.id);
          try {
            const now = ref.value;
            if (now) {
              state.source.update(ref, {...now, description: text ? text : undefined});
              state.source.commit(ref);
            }
          } finally {
            ref.dispose();
          }
          return;  // the list draws the row again, with the note
        }
        box.remove();
        if (noteText) noteText.style.display = '';
      };
      for (const type of ['mousedown', 'click', 'dblclick', 'contextmenu', 'keyup', 'keypress', 'wheel']) {
        box.addEventListener(type, event => event.stopPropagation());
      }
      box.addEventListener('keydown', event => {
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); close(false); }
        else if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); close(true); }
      });
      box.addEventListener('blur', () => close(true));
      element.appendChild(box);
      box.focus();
      box.setSelectionRange(box.value.length, box.value.length);
    };
    if (!state.source.readonly) {
      const noteButton = document.createElement('button');
      noteButton.type = 'button';
      noteButton.className = 'nge-ann-row-note';
      noteButton.title = ngeNoteOf(annotation) ? 'Edit the note' : 'Add a note';
      noteButton.setAttribute('aria-label', noteButton.title);
      noteButton.innerHTML = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M11.3 2.3a1.4 1.4 0 0 1 2 2L5.6 12 2.5 13.5 4 10.4z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';
      noteButton.classList.toggle('nge-ann-row-note--has', !!ngeNoteOf(annotation));
      noteButton.addEventListener('mousedown', event => event.stopPropagation());
      noteButton.addEventListener('click', event => {
        event.stopPropagation();
        event.preventDefault();
        openNoteEditor();
      });
      rowActions.appendChild(noteButton);
    }

    const columnWidths: number[] = [];

    let numRows = 0;
    visitTransformedAnnotationGeometry(annotation, chunkTransform, (layerPosition, isVector) => {
      isVector;
      ++numRows;
      const position = document.createElement('div');
      position.className = 'neuroglancer-annotation-position';
      element.appendChild(position);
      let i = 0;

      const addDims =
          (viewDimensionIndices: readonly number[], layerDimensionIndices: readonly number[]) => {
            for (const viewDim of viewDimensionIndices) {
              const layerDim = layerDimensionIndices[viewDim];
              if (layerDim !== -1) {
                const coord = Math.floor(layerPosition[layerDim]);
                const coordElement = document.createElement('div');
                const text = coord.toString();
                coordElement.textContent = text;
                coordElement.classList.add('neuroglancer-annotation-coordinate');
                coordElement.style.gridColumn = `dim ${i + 1}`;
                columnWidths[i] = Math.max(columnWidths[i] || 0, text.length);
                position.appendChild(coordElement);
              }
              ++i;
            }
          };
      addDims(
          globalDimensionIndices, chunkTransform.modelTransform.globalToRenderLayerDimensions);
      addDims(
          localDimensionIndices, chunkTransform.modelTransform.localToRenderLayerDimensions);
      maybeAddDeleteButton();
    });
    if (ngeNoteOf(annotation)) {
      ++numRows;
      const description = document.createElement('div');
      description.classList.add('neuroglancer-annotation-description');
      description.textContent = ngeNoteOf(annotation);
      element.appendChild(description);
      noteText = description;
    }
    icon.style.gridRow = `span ${numRows}`;
    // Added last and pinned to the first line: placed earlier, it sat in the
    // last column and pushed the coordinates down onto a second line.
    rowActions.style.gridRow = `1 / span ${numRows}`;
    element.appendChild(rowActions);
    element.addEventListener('mouseenter', () => {
      layer.selectAnnotation(state, annotation.id, false);
    });
    element.addEventListener('action:select-position', event => {
      event.stopPropagation();
      layer.selectAnnotation(state, annotation.id, 'toggle');
    });
    element.addEventListener('action:pin-annotation', event => {
      event.stopPropagation();
      layer.selectAnnotation(state, annotation.id, true);
    });
    element.addEventListener('action:move-to-annotation', event => {
      event.stopPropagation();
      event.preventDefault();
      const {layerRank} = chunkTransform;
      const chunkPosition = new Float32Array(layerRank);
      const layerPosition = new Float32Array(layerRank);
      getCenterPosition(chunkPosition, annotation);
      matrix.transformPoint(
          layerPosition, chunkTransform.chunkToLayerTransform, layerRank + 1, chunkPosition,
          layerRank);
      setLayerPosition(layer, chunkTransform, layerPosition);
    });
    return [element, columnWidths];
  }